import { IPublicSession, userSession } from 'state/session';
import { createScheduledSession as createScheduledSessionApi, getScheduledSessions } from 'utils/network/schedule';
import type { ISessionData } from 'data/schema';
import { State } from 'utils/state';
import { sendApiRequest } from 'utils/network';
import { tenantConfig } from 'utils/tenant';

export function endScheduledSessionCallback(state: State<IPublicSession>) {
  state.on('session:end', () => {
    autoScaleInstances();
  });
}

function autoScaleInstances() {
  // AVS-4219: the schedule-planner Lambda is deferred 3-6 months and currently
  // 500s on every call (Cognito invalid_grant). Gate on the existing `scheduler`
  // flag so the API is only hit when the scheduling feature is actually enabled.
  // Remove this guard once the Lambda is provisioned and the feature ships.
  if (!tenantConfig().isAllowed('scheduler')) {
    return;
  }
  // Fire-and-forget. While the Lambda is broken we must not surface any error:
  // suppressToast silences the toast and the wrapper's console.error; the catch
  // swallows the rejected promise so it never bubbles up as unhandled.
  sendApiRequest(`/schedule-planner`, { method: 'POST', suppressToast: true }).catch(() => {});
}

/**
 * Format date as YYYY-MM-DD
 */
export function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Combine date and time into ISO 8601 format with timezone
 * Format: YYYY-MM-DDTHH:mm:ss.sssZ (UTC)
 * This format is optimal for DynamoDB queries, filtering, and sorting
 */
export function createScheduledDateTime(date: string, time: string): string {
  const dateTimeString = `${date}T${time}:00`;
  const localDate = new Date(dateTimeString);
  return localDate.toISOString();
}

/**
 * Get the user's current timezone
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/**
 * Schedule a session for a specific date and time
 * Saves to userSession which automatically syncs to DynamoDB
 */
export function scheduleSession(date: string, time: string) {
  const scheduledStartTime = createScheduledDateTime(date, time);

  // Save to userSession - this will automatically sync to DynamoDB
  userSession().setValues({
    scheduledStartTime,
  });
}

/**
 * Get the currently scheduled start time from userSession
 */
export function getScheduledStartTime(): string | undefined {
  return userSession().get('scheduledStartTime');
}

/**
 * Clear the scheduled start time from userSession
 */
export function clearScheduledStartTime(): void {
  userSession().setValues({
    scheduledStartTime: undefined,
  });
}

/**
 * Parse ISO 8601 UTC timestamp to local date and time strings for form inputs
 * Returns { date: 'YYYY-MM-DD', time: 'HH:mm' } in user's local timezone
 * Returns undefined if the input is invalid
 */
export function parseScheduledDateTime(isoString: string): { date: string; time: string } | undefined {
  try {
    const date = new Date(isoString);

    // Validate that the date is valid
    if (isNaN(date.getTime())) {
      return undefined;
    }

    // Extract date components in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return {
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}`,
    };
  } catch {
    // Return undefined if parsing fails
    return undefined;
  }
}

/**
 * Check if a scheduled session is more than the specified minutes in the future
 * Returns true if the scheduled time is more than the specified minutes from now
 * @param scheduledStartTime - ISO 8601 timestamp
 * @param thresholdMinutes - Number of minutes to check (default: 60)
 */
export function isScheduledTimeFarInFuture(scheduledStartTime: string, thresholdMinutes: number = 60): boolean {
  try {
    const scheduledTime = new Date(scheduledStartTime);
    const now = new Date();

    // Validate that the scheduled time is valid
    if (isNaN(scheduledTime.getTime())) {
      return false;
    }

    const diffInMinutes = (scheduledTime.getTime() - now.getTime()) / (1000 * 60);
    return diffInMinutes > thresholdMinutes;
  } catch {
    return false;
  }
}

/**
 * Create a new scheduled session by cloning current session data
 * Creates a completely new session without modifying the current active session
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Time string in HH:mm format
 * @returns The newly created scheduled session
 */
export async function createNewScheduledSession(date: string, time: string): Promise<ISessionData> {
  const currentSession = userSession().get();
  const scheduledStartTime = createScheduledDateTime(date, time);

  // Clone session data without id, endedAt (server will assign id and endedAt)
  const { id: _id, endedAt: _endedAt, ...sessionData } = currentSession;

  // Create the new scheduled session via API
  const newSession = await createScheduledSessionApi({
    ...sessionData,
    isShared: false, // New session is not yet shared
    sharingType: 'scheduled', // Mark as scheduled session for instance scaler GSI
    scheduledStartTime,
  });

  // Reset the current session's sharing type and clear any scheduled time
  // This ensures the current session remains active without scheduled time
  userSession().setValues({
    sharingType: undefined,
    scheduledStartTime: undefined,
  });

  return newSession;
}

/**
 * Filter sessions to only include future sessions
 * @param sessions - Array of session data
 * @returns Array of sessions that are scheduled in the future
 */
export function filterFutureSessions(sessions: ISessionData[]): ISessionData[] {
  const now = new Date();
  return sessions.filter((session) => {
    if (!session.scheduledStartTime) return false;
    const sessionDate = new Date(session.scheduledStartTime);
    return sessionDate > now;
  });
}

/**
 * Filter sessions by a specific date
 * @param sessions - Array of session data
 * @param filterDate - Date to filter by
 * @returns Array of sessions scheduled on the specified date
 */
export function filterSessionsByDate(sessions: ISessionData[], filterDate: Date): ISessionData[] {
  return sessions.filter((session) => {
    if (!session.scheduledStartTime) return false;
    const sessionDate = new Date(session.scheduledStartTime);
    return (
      sessionDate.getDate() === filterDate.getDate() &&
      sessionDate.getMonth() === filterDate.getMonth() &&
      sessionDate.getFullYear() === filterDate.getFullYear()
    );
  });
}

/**
 * Filter sessions by month and year
 * @param sessions - Array of session data
 * @param currentDate - Date containing the month and year to filter by
 * @returns Array of sessions scheduled in the specified month
 */
export function filterSessionsByMonth(sessions: ISessionData[], currentDate: Date): ISessionData[] {
  return sessions.filter((session) => {
    if (!session.scheduledStartTime) return false;
    const sessionDate = new Date(session.scheduledStartTime);
    return sessionDate.getMonth() === currentDate.getMonth() && sessionDate.getFullYear() === currentDate.getFullYear();
  });
}

/**
 * Sort sessions by their scheduled start time (ascending)
 * @param sessions - Array of session data
 * @returns Sorted array of sessions
 */
export function sortSessionsByTime(sessions: ISessionData[]): ISessionData[] {
  return [...sessions].sort((a, b) => {
    const aTime = a.scheduledStartTime ? new Date(a.scheduledStartTime).getTime() : 0;
    const bTime = b.scheduledStartTime ? new Date(b.scheduledStartTime).getTime() : 0;
    return aTime - bTime;
  });
}

/**
 * Group sessions by date
 * @param sessions - Array of session data
 * @returns Map with date keys (YYYY-M-D) and arrays of sessions for each date
 */
export function groupSessionsByDate(sessions: ISessionData[]): Map<string, ISessionData[]> {
  const groups = new Map<string, ISessionData[]>();

  sessions.forEach((session) => {
    if (!session.scheduledStartTime) return;

    const sessionDate = new Date(session.scheduledStartTime);
    const dateKey = `${sessionDate.getFullYear()}-${sessionDate.getMonth()}-${sessionDate.getDate()}`;

    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(session);
  });

  return groups;
}

/**
 * Check if a session can be started based on tenant configuration time window
 * @param session - Session data with scheduled start time
 * @param startWindowBefore - Minutes before scheduled time when session can start (default: 5)
 * @param startWindowAfter - Minutes after scheduled time when session can start (default: 90)
 * @returns True if the session is within the start window
 */
export function canStartSession(
  session: ISessionData,
  startWindowBefore: number = 5,
  startWindowAfter: number = 90
): boolean {
  if (!session.scheduledStartTime) return false;

  const now = new Date();
  const scheduledTime = new Date(session.scheduledStartTime);
  const timeDiffMs = now.getTime() - scheduledTime.getTime();
  const timeDiffMinutes = timeDiffMs / (1000 * 60);

  // Allow starting from X minutes before to Y minutes after scheduled time
  return timeDiffMinutes >= -startWindowBefore && timeDiffMinutes <= startWindowAfter;
}

/**
 * Get display name for a session
 * Returns car display name, VIN/marketing code, or fallback text
 * @param session - Session data
 * @param fallback - Fallback text if no name is available (default: 'Scheduled Session')
 * @returns Display name for the session
 */
export function getSessionDisplayName(session: ISessionData, fallback: string = 'Scheduled Session'): string {
  return session.carDisplayName || session.vinOrMarketingCode || fallback;
}

/**
 * Format an ISO 8601 scheduled time for display in user-facing messages
 * (e.g. toast notifications). Returns an empty string for invalid input so
 * callers can safely fall back to a generic message.
 * @param scheduledStartTime - ISO 8601 timestamp
 * @param locale - Optional BCP-47 locale; defaults to runtime locale when omitted
 */
export function formatScheduledTimeForDisplay(scheduledStartTime: string, locale?: string): string {
  if (!scheduledStartTime) return '';
  const date = new Date(scheduledStartTime);
  if (isNaN(date.getTime())) return '';
  try {
    return date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    // Invalid BCP-47 locale tag — fall back to runtime default.
    return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }
}

/**
 * Check if a new scheduled time overlaps with existing sessions
 * Overlap is defined as: new session starts within 20 minutes of an existing session's start time
 * @param newScheduledTime - ISO 8601 timestamp for the new session
 * @param existingSessions - Array of existing scheduled sessions
 * @param overlapWindowMinutes - Minutes to check for overlap (default: 20)
 * @param excludeSessionId - Optional id to skip during comparison; used when editing
 *   an existing session so it does not flag a self-overlap.
 * @returns The overlapping session if found, undefined otherwise
 */
export function checkScheduleOverlap(
  newScheduledTime: string,
  existingSessions: ISessionData[],
  overlapWindowMinutes: number = 20,
  excludeSessionId?: string
): ISessionData | undefined {
  const newTime = new Date(newScheduledTime);

  // Validate that the new time is valid
  if (isNaN(newTime.getTime())) {
    return undefined;
  }

  // Check each existing session for overlap
  for (const session of existingSessions) {
    if (!session.scheduledStartTime) continue;
    if (excludeSessionId && session.id === excludeSessionId) continue;

    const existingTime = new Date(session.scheduledStartTime);

    // Validate existing time
    if (isNaN(existingTime.getTime())) continue;

    // Calculate time difference in minutes
    const timeDiffMs = Math.abs(newTime.getTime() - existingTime.getTime());
    const timeDiffMinutes = timeDiffMs / (1000 * 60);

    // If within overlap window, return the conflicting session
    if (timeDiffMinutes <= overlapWindowMinutes) {
      return session;
    }
  }

  return undefined;
}

/**
 * Fetch the user's scheduled sessions and check whether `scheduledStartTime`
 * conflicts with any of them. Wraps the network call + overlap check so the
 * component layer does not have to coordinate both.
 *
 * The frontend is the source of truth for overlap validation: the backend
 * enforces overlap on POST /schedule only — PUT /schedule/{id} does not
 * validate. Removing this check would silently allow conflicting edits.
 *
 * @param scheduledStartTime - ISO 8601 timestamp the user is trying to save
 * @param excludeSessionId - Session id to skip (the one being edited)
 * @returns The conflicting session, or undefined when no conflict
 */
export async function fetchScheduleConflict(
  scheduledStartTime: string,
  excludeSessionId?: string
): Promise<ISessionData | undefined> {
  const existingSessions = await getScheduledSessions();
  return checkScheduleOverlap(scheduledStartTime, existingSessions, 20, excludeSessionId);
}
